import { useQuery } from '@tanstack/react-query';
import {
  lexwareArchiveUploadListSchema,
  lexwareArchiveUploadSchema,
  type LexwareArchiveUploadStatus,
} from '@erp/shared';
import { apiFetch } from '@/lib/api-client';

const IMPORT_PATH = '/api/v1/legacy-documents/lexware-imports';
const POLL_INTERVAL_MS = 2000;
export const lexwareArchiveUploadKey = (id: string | undefined) => ['lexware-archive-upload', id];
export const lexwareArchiveUploadsKey = ['lexware-archive-uploads'];
const isRunning = (status: LexwareArchiveUploadStatus) =>
  status === 'QUEUED' || status === 'EXTRACTING' || status === 'IMPORTING';

export function getLexwareArchiveUpload(id: string, signal?: AbortSignal) {
  return apiFetch(`${IMPORT_PATH}/${encodeURIComponent(id)}`, lexwareArchiveUploadSchema, {
    ...(signal === undefined ? {} : { signal }),
  });
}
export function createLexwareArchiveUpload(
  file: File,
  fileFingerprint: string,
  signal: AbortSignal,
) {
  return apiFetch(IMPORT_PATH, lexwareArchiveUploadSchema, {
    method: 'POST',
    body: { fileName: file.name, fileSize: file.size, fileFingerprint },
    signal,
  });
}
export function appendLexwareArchiveChunk(
  id: string,
  offset: number,
  sha256: string,
  chunk: Blob,
  signal: AbortSignal,
) {
  const query = new URLSearchParams({ offset: String(offset), sha256 });
  return apiFetch(
    `${IMPORT_PATH}/${encodeURIComponent(id)}/chunks?${query}`,
    lexwareArchiveUploadSchema,
    {
      method: 'PUT',
      rawBody: chunk,
      signal,
    },
  );
}
export function finishLexwareArchiveUpload(id: string) {
  return apiFetch(`${IMPORT_PATH}/${encodeURIComponent(id)}/finish`, lexwareArchiveUploadSchema, {
    method: 'POST',
    body: {},
  });
}
export function confirmLexwareArchiveUpload(id: string, previewHash: string) {
  return apiFetch(`${IMPORT_PATH}/${encodeURIComponent(id)}/confirm`, lexwareArchiveUploadSchema, {
    method: 'POST',
    body: { previewHash },
  });
}
export function retryLexwareArchiveUpload(id: string, previewHash: string | null) {
  return apiFetch(`${IMPORT_PATH}/${encodeURIComponent(id)}/retry`, lexwareArchiveUploadSchema, {
    method: 'POST',
    body: previewHash === null ? {} : { previewHash },
  });
}
export function useLexwareArchiveUploads(enabled: boolean) {
  return useQuery({
    queryKey: lexwareArchiveUploadsKey,
    enabled,
    queryFn: () => apiFetch(IMPORT_PATH, lexwareArchiveUploadListSchema),
    refetchInterval: (query) =>
      query.state.data?.data.some((upload) => isRunning(upload.status)) ? POLL_INTERVAL_MS : false,
  });
}
export function useLexwareArchiveUpload(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: lexwareArchiveUploadKey(id),
    enabled: enabled && id !== undefined,
    staleTime: POLL_INTERVAL_MS,
    queryFn: () => getLexwareArchiveUpload(id ?? ''),
    refetchInterval: (query) =>
      query.state.data !== undefined && isRunning(query.state.data.status)
        ? POLL_INTERVAL_MS
        : false,
  });
}
